import { useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  ArchiveIcon,
  CashIcon,
  CreditCardIcon,
  PencilAltIcon,
  XCircleIcon,
} from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { withNotification } from 'components/Notification';
import { useStore } from 'context/store-context';
import { GET_CLAIM } from 'gql/queries';
import {
  DENY_CLAIM,
  OTHER_CLAIM,
  REFUND_CLAIM,
  REORDER_CLAIM,
} from 'gql/mutations';
import { Claim, ClaimItemValues } from 'types/claim';
import { Order } from 'types/order';
import { apiErrorHandler, NotificationTypes } from 'utils';
import {
  getRequestReturnTo,
  withRequestReturnTo,
} from 'utils/requestNavigation';
import { Denyclaim } from './DenyClaim';
import { Other } from './Other';
import { Refund } from './Refund';
import { Reorder } from './Reorder';

type NewIssueResolutionProps = {
  claimId: string;
  order: Order;
  returnTo: string;
  showNotification: (type: NotificationTypes, message: string) => void;
};

const tabs = [
  { name: 'In Review', icon: CreditCardIcon },
  { name: 'Reorder', icon: ArchiveIcon },
  { name: 'Refund', icon: CashIcon },
  { name: 'Other', icon: PencilAltIcon },
  { name: 'Deny Issue', icon: XCircleIcon },
];

const availableQuantity = (item: ClaimItemValues) =>
  Number(item.quantity ?? 0) - Number(item.settlement?.quantity ?? 0);

export const NewIssueResolution = withNotification(
  ({ claimId, order, returnTo, showNotification }: NewIssueResolutionProps) => {
    const { storeId } = useStore();
    const navigate = useNavigate();
    const destination = getRequestReturnTo('', returnTo, '/requests');
    const [getClaim] = useLazyQuery(GET_CLAIM);
    const [reorderClaim] = useMutation(REORDER_CLAIM);
    const [refundClaim] = useMutation(REFUND_CLAIM);
    const [otherClaim] = useMutation(OTHER_CLAIM);
    const [denyClaim] = useMutation(DENY_CLAIM);
    const [claim, setClaim] = useState<Claim | null>(null);
    const [items, setItems] = useState<ClaimItemValues[]>([]);
    const [selectedTab, setSelectedTab] = useState('In Review');
    const [loading, setLoading] = useState(true);
    const [readError, setReadError] = useState(false);
    const [actionError, setActionError] = useState('');
    const [retry, setRetry] = useState(0);
    const [saving, setSaving] = useState(false);
    const submitting = useRef(false);
    const scope = useRef(0);
    const pendingReorderWindow = useRef<Window | null>(null);

    useEffect(() => {
      let active = true;
      scope.current += 1;
      setLoading(true);
      setReadError(false);
      setClaim(null);
      setItems([]);
      setSelectedTab('In Review');
      setActionError('');
      getClaim({ variables: { storeId, id: claimId } })
        .then((result) => {
          if (!active) return;
          const found = result.data?.claims?.[0] as Claim | undefined;
          if (
            result.error ||
            !found ||
            found.id !== claimId ||
            !Array.isArray(found.items) ||
            (found.storeId && found.storeId !== storeId) ||
            (found.orderId && found.orderId !== order.id)
          ) {
            setReadError(true);
            return;
          }
          setClaim({ ...found, order: found.order ?? order });
          setItems(
            found.items
              .filter((item) => availableQuantity(item) > 0)
              .map((item) => ({
                ...item,
                quantity: availableQuantity(item),
                // Editable rows already contain the unsettled quantity.
                settlement: undefined,
              })),
          );
        })
        .catch(() => {
          if (active) setReadError(true);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
        scope.current += 1;
        pendingReorderWindow.current?.close();
        pendingReorderWindow.current = null;
      };
    }, [getClaim, storeId, claimId, order, retry]);

    const updateQuantity = (value: string, item: ClaimItemValues) => {
      if (submitting.current) return;
      setItems((current) =>
        current.map((candidate) =>
          candidate.id === item.id
            ? { ...candidate, quantity: Number(value) }
            : candidate,
        ),
      );
      setActionError('');
    };

    const removeItem = (item: ClaimItemValues) => {
      if (submitting.current) return;
      setItems((current) =>
        current.filter((candidate) => candidate.id !== item.id),
      );
      setActionError('');
    };

    const submit = async (
      action: 'Reorder' | 'Refund' | 'Other' | 'Deny Issue',
      input: any,
    ) => {
      if (submitting.current || !claim || loading || readError) return;
      if (action !== 'Deny Issue') {
        if (!items.length) {
          setActionError('Select at least one item to resolve.');
          return;
        }
        const valid = items.every((item) => {
          const original = claim.items.find(
            (candidate) => candidate.id === item.id,
          );
          return (
            original &&
            item.id &&
            Number.isInteger(item.quantity) &&
            Number(item.quantity) > 0 &&
            Number(item.quantity) <= availableQuantity(original)
          );
        });
        if (
          !valid ||
          new Set(items.map((item) => item.id)).size !== items.length
        ) {
          setActionError(
            'Choose a whole quantity between 1 and the available quantity for each item.',
          );
          return;
        }
      }
      submitting.current = true;
      setSaving(true);
      setActionError('');
      const actionScope = scope.current;
      let reorderWindow: Window | null = null;
      try {
        if (action === 'Reorder') {
          // Reserve during the click; browsers may block new tabs after awaiting the mutation.
          reorderWindow = window.open('about:blank', '_blank');
          pendingReorderWindow.current = reorderWindow;
          if (reorderWindow) {
            reorderWindow.opener = null;
            const referrerPolicy = reorderWindow.document.createElement('meta');
            referrerPolicy.name = 'referrer';
            referrerPolicy.content = 'no-referrer';
            reorderWindow.document.head.appendChild(referrerPolicy);
            reorderWindow.document.title = 'Creating replacement order';
          }
        }
        const mutation = {
          Reorder: reorderClaim,
          Refund: refundClaim,
          Other: otherClaim,
          'Deny Issue': denyClaim,
        }[action];
        const result = await mutation({
          variables: {
            input: {
              ...input,
              storeId,
              id: claimId,
              ...(action === 'Deny Issue'
                ? {}
                : {
                    items: items.map((item) => ({
                      id: item.id,
                      quantity: item.quantity,
                    })),
                  }),
            },
          },
        });
        if (result.errors?.length) throw result.errors[0];
        if (!result.data)
          throw new Error(
            'The resolution could not be confirmed. Open the Issue to check its status.',
          );
        if (scope.current !== actionScope) return;
        const orderUrl = result.data.reorderClaim?.[0]?.orderUrl;
        if (reorderWindow && !reorderWindow.closed && orderUrl) {
          try {
            reorderWindow.location.href = orderUrl;
          } catch {
            reorderWindow.close();
          }
          pendingReorderWindow.current = null;
        }
        navigate(destination);
      } catch (error) {
        if (scope.current !== actionScope) return;
        const message =
          apiErrorHandler(error as any)?.message ||
          'The resolution could not be saved. Try again.';
        setActionError(message);
        showNotification(NotificationTypes.error, message);
      } finally {
        if (reorderWindow && pendingReorderWindow.current === reorderWindow) {
          reorderWindow.close();
          pendingReorderWindow.current = null;
        }
        submitting.current = false;
        if (scope.current === actionScope) setSaving(false);
      }
    };

    const panelProps = {
      claimItems: items,
      staticClaimItems: claim?.items ?? [],
      claim: claim ?? undefined,
      order,
      loading: saving,
      handleRemoveItem: removeItem,
      handleQuantityUpdate: updateQuantity,
    };

    return (
      <section aria-label="Issue resolution" className="mt-6">
        <div className="flex flex-wrap items-start justify-between gap-4 rounded-md border border-green-200 bg-green-50 p-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Issue created
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Review the Issue or choose a resolution for order {order.number}.
            </p>
          </div>
          <Button variant="outlined" onClick={() => navigate(destination)}>
            Skip Resolution
          </Button>
        </div>

        {loading && (
          <p role="status" className="py-6 text-sm text-gray-600">
            Loading resolution details…
          </p>
        )}
        {readError && (
          <div
            role="alert"
            className="mt-4 rounded-md border border-red-200 bg-red-50 p-4"
          >
            <p className="text-sm text-red-800">
              The Issue is already created, but its resolution details could not
              be loaded.
            </p>
            <Button
              className="mt-3"
              variant="outlined"
              onClick={() => setRetry((value) => value + 1)}
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !readError && claim && (
          <div className="mt-6">
            <nav
              aria-label="Issue resolution options"
              className="flex flex-wrap gap-2 border-b border-gray-200 pb-3"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  type="button"
                  aria-pressed={selectedTab === tab.name}
                  disabled={saving}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 ${
                    selectedTab === tab.name
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    if (!submitting.current) {
                      setSelectedTab(tab.name);
                      setActionError('');
                    }
                  }}
                >
                  <tab.icon className="h-5 w-5" aria-hidden="true" />
                  {tab.name}
                </button>
              ))}
            </nav>
            {actionError && (
              <p
                role="alert"
                className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-800"
              >
                {actionError}
              </p>
            )}
            {saving && (
              <p role="status" className="mt-3 text-sm text-gray-600">
                Saving resolution…
              </p>
            )}
            {selectedTab === 'In Review' && (
              <div className="mt-4 rounded-md border border-gray-200 p-5">
                <h3 className="text-base font-semibold text-gray-900">
                  Review the Issue
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  The Issue is saved. Open its details to review the request,
                  add notes, and manage its status.
                </p>
                <Button
                  className="mt-4"
                  onClick={() =>
                    navigate(
                      withRequestReturnTo(`/claims/${claimId}`, destination),
                      {
                        state: { returnLocation: destination },
                      },
                    )
                  }
                >
                  Keep in review
                </Button>
              </div>
            )}
            <fieldset disabled={saving}>
              {selectedTab === 'Reorder' && (
                <Reorder
                  {...panelProps}
                  handleReorderItems={(input) => submit('Reorder', input)}
                />
              )}
              {selectedTab === 'Refund' && (
                <Refund
                  {...panelProps}
                  handleRefundItems={(input) => submit('Refund', input)}
                />
              )}
              {selectedTab === 'Other' && (
                <Other
                  {...panelProps}
                  handleOtherItems={(input) => submit('Other', input)}
                />
              )}
              {selectedTab === 'Deny Issue' && (
                <Denyclaim
                  {...panelProps}
                  handleDenyItems={(input) => submit('Deny Issue', input)}
                />
              )}
            </fieldset>
          </div>
        )}
      </section>
    );
  },
);
